import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import WithLoader from 'components/WithLoader';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { vocabularyTestActions } from 'pages/VocabularyTest/store/vocabularyTest.slice';
import TestHistory from 'pages/VocabularyTest/components/TestHistory';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { levelOf } from 'pages/VocabularyTest/cefr';
import { useVocabularyTestScope } from 'pages/VocabularyTest/useVocabularyTestScope';
import VocabularyTestHeroCard from 'pages/VocabularyTest/components/VocabularyTestHeroCard';
import VocabularyTestProgressCard from 'pages/VocabularyTest/components/VocabularyTestProgressCard';
import VocabularyTestActionPlanCard from 'pages/VocabularyTest/components/VocabularyTestActionPlanCard';
import VocabularyTestInProgressBanner from 'pages/VocabularyTest/components/VocabularyTestInProgressBanner';
import VocabularyTestHowItWorks from 'pages/VocabularyTest/components/VocabularyTestHowItWorks';
import {
  VocabularyTestHistory,
  VocabularyTestHistoryPage,
  VocabularyTypeRecord,
} from 'app/types';
import { NotificationCenterContext } from 'components/NotificationCenter';
import { axiosInstance } from 'setup/axiosInstance';

// BE caps page size at 100; that's plenty for "every completed test" — even
// power users won't churn 100+ vocabulary tests. Used only for the trend
// chart on the landing; the table below stays paginated.
const TREND_FETCH_SIZE = 100;

const sumLearned = (data: VocabularyTypeRecord[]) =>
  data.reduce((acc, record) => acc + Number(record.learned || 0), 0);

const VocabularyTest: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pushNotification } = useContext(NotificationCenterContext);
  const {
    loading: vocabularyTestHistoryLoading,
    data: vocabularyTestHistoryData,
  } = useAppSelector((store) => store.vocabularyTest.vocabularyTestHistory);

  const { completedTestResult } = useAppSelector(
    (store) => store.vocabularyTest,
  );
  const deleteTestHistorySuccessCount = useAppSelector(
    (store) => store.vocabularyTest.deleteTestHistorySuccessCount,
  );
  const deleteNotificationCountRef = useRef(deleteTestHistorySuccessCount);
  const deleteForFreshStartCountRef = useRef(deleteTestHistorySuccessCount);
  const scope = useVocabularyTestScope();
  // `scope.selection` is a fresh array on every render, so it must not sit in
  // effect dependency arrays (it would make the effect re-run every render and,
  // combined with a dispatch, spin into "Maximum update depth exceeded"). Keep
  // the latest value in a ref and read it inside effects instead.
  const scopeSelectionRef = useRef(scope.selection);
  scopeSelectionRef.current = scope.selection;
  const [testSettingsOpen, setTestSettingsOpen] = useState(false);
  const [confirmFreshOpen, setConfirmFreshOpen] = useState(false);
  const [pendingFreshStart, setPendingFreshStart] = useState(false);

  const isInitialHistoryLoading =
    vocabularyTestHistoryLoading && !vocabularyTestHistoryData.length;

  const inProgressTest = useMemo(
    () =>
      vocabularyTestHistoryData.find((test) => test.status === 'IN_PROGRESS'),
    [vocabularyTestHistoryData],
  );
  const hasInProgressTest = !!inProgressTest;

  const { data: inProgressTopics } = useAppSelector(
    (store) => store.vocabularyTest.vocabularyTestTopics,
  );
  // The landing banner needs topic pass/current/upcoming status to draw its
  // progress bar — the same data the runner uses. `/current` is a read-only
  // "give me the active test's current batch" call when a test already
  // exists (see BE `getCurrentOrNew`), so calling it here doesn't advance
  // anything; it just also fetches `/topics` as a side effect.
  useEffect(() => {
    if (hasInProgressTest) {
      dispatch(vocabularyTestActions.vocabularyTestInProgressGet());
    }
    // Re-fetch only when which test is in progress changes, not on every
    // render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInProgressTest, inProgressTest?.id]);

  const latestCompletedTest = useMemo(
    () => vocabularyTestHistoryData.find((test) => test.status === 'DONE'),
    [vocabularyTestHistoryData],
  );

  const vocabulary = useAppSelector((store) => store.common.vocabulary.data);
  const vocabularyLearnedCount = useMemo(
    () => sumLearned(vocabulary),
    [vocabulary],
  );

  // Full completed-test history for the "Зростання запасу" trend chart. The
  // paginated `vocabularyTestHistoryData` only covers the visible 5 rows of
  // the table below, which truncates the line to the last page. Refetched
  // on mount and whenever a test finishes or a row gets deleted.
  const [allCompletedHistory, setAllCompletedHistory] = useState<
    VocabularyTestHistory[]
  >([]);
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<VocabularyTestHistoryPage>('/test-vocabulary/history/page', {
        params: { page: 0, size: TREND_FETCH_SIZE },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setAllCompletedHistory(
          (data.content || []).filter((t) => t.status === 'DONE'),
        );
      })
      .catch(() => {
        // Trend chart hides itself when history has fewer than 2 points, so a
        // failed fetch degrades to "no chart" — no need to surface an error.
      });
    return () => {
      cancelled = true;
    };
  }, [completedTestResult, deleteTestHistorySuccessCount]);

  const goToRunScreen = () => navigate('/' + pagePaths.vocabularyTestRun);

  const startTesting = () => {
    dispatch(
      vocabularyTestActions.startTesting(
        scope.selection.length ? { selection: scope.selection } : undefined,
      ),
    );
    goToRunScreen();
  };

  const handleStartFresh = () => {
    if (!inProgressTest) {
      startTesting();
      return;
    }
    setPendingFreshStart(true);
    // Mark this deletion as part of the "Start new" flow so the success toast
    // for it is suppressed by the notification effect below.
    deleteForFreshStartCountRef.current = deleteTestHistorySuccessCount + 1;
    dispatch(vocabularyTestActions.deleteTestHistory(inProgressTest.id));
    setConfirmFreshOpen(false);
  };

  useEffect(() => {
    dispatch(vocabularyTestActions.vocabularyTestHistoryGet({ page: 0 }));
  }, [dispatch]);

  // If a test result arrives while we're still on the landing (rare — happens
  // when the user finishes a test in another tab and comes back), forward them
  // to the result page same as before.
  useEffect(() => {
    if (completedTestResult) {
      navigate(
        fillUrlIds(pagePaths.vocabularyTestResult, {
          vocabularyTestResultId: String(completedTestResult.id),
        }),
      );
      dispatch(vocabularyTestActions.completedTestResultClose());
    }
  }, [completedTestResult, dispatch, navigate]);

  useEffect(() => {
    if (deleteTestHistorySuccessCount > deleteNotificationCountRef.current) {
      if (
        deleteTestHistorySuccessCount !== deleteForFreshStartCountRef.current
      ) {
        pushNotification(
          'Історію тестування видалено',
          'success',
          undefined,
          'center',
        );
      }
    }
    deleteNotificationCountRef.current = deleteTestHistorySuccessCount;
  }, [deleteTestHistorySuccessCount, pushNotification]);

  // After the IN_PROGRESS deletion finishes, kick off the actual startTesting
  // and route into the runner.
  useEffect(() => {
    if (
      pendingFreshStart &&
      deleteTestHistorySuccessCount >= deleteForFreshStartCountRef.current
    ) {
      const selection = scopeSelectionRef.current;
      dispatch(
        vocabularyTestActions.startTesting(
          selection.length ? { selection } : undefined,
        ),
      );
      setPendingFreshStart(false);
      goToRunScreen();
    }
    // scope.selection is read via ref (it's a new array each render — see
    // scopeSelectionRef) so it stays OUT of deps; goToRunScreen/navigate are
    // stable. Deps kept minimal to avoid re-firing (and looping) every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTestHistorySuccessCount, pendingFreshStart]);

  if (isInitialHistoryLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        <UIContainer>
          <BreadCrumbs />
        </UIContainer>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            minHeight: 240,
          }}
        >
          <WithLoader loading>
            <></>
          </WithLoader>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>

      <UIContainer>
        <Stack spacing={2.5} pb={5} pt={2}>
          {hasInProgressTest && inProgressTest && (
            <VocabularyTestInProgressBanner
              inProgressTest={inProgressTest}
              testTopics={inProgressTopics}
              onContinue={goToRunScreen}
              onStartFresh={() => setConfirmFreshOpen(true)}
            />
          )}

          {!hasInProgressTest && latestCompletedTest && (
            <VocabularyTestHeroCard
              learnedCount={vocabularyLearnedCount}
              history={vocabularyTestHistoryData}
              onStart={startTesting}
              onToggleScope={() => setTestSettingsOpen((value) => !value)}
              scopeOpen={testSettingsOpen}
              scope={scope}
            />
          )}

          {!hasInProgressTest && !latestCompletedTest && (
            <VocabularyTestHowItWorks
              onStart={startTesting}
              onToggleScope={() => setTestSettingsOpen((value) => !value)}
              scopeOpen={testSettingsOpen}
              scope={scope}
            />
          )}

          {latestCompletedTest && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '1fr 1fr',
                },
              }}
            >
              <VocabularyTestProgressCard history={allCompletedHistory} />
              <VocabularyTestActionPlanCard
                currentLevel={levelOf(vocabularyLearnedCount).id}
                learnedCount={vocabularyLearnedCount}
              />
            </Box>
          )}

          <TestHistory />
        </Stack>
      </UIContainer>

      <Dialog
        disableScrollLock
        open={confirmFreshOpen}
        onClose={() => setConfirmFreshOpen(false)}
      >
        <DialogTitle>{'Почати новий тест?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {
              'Поточний тест у процесі буде видалений. Прогрес не буде збережено в історії.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFreshOpen(false)}>
            {'Скасувати'}
          </Button>
          <Button color="error" onClick={handleStartFresh}>
            {'Видалити і почати'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VocabularyTest;
